import { get, getTrackingString } from '../../utils/object-helpers';

export default class ModelHelper {
    constructor(quickviewResp, itemCard) {
        this.resp = quickviewResp;
        this.itemCard = itemCard;
    }
    getMenu(menuId) {
        return this.getSelectMenus().find(selectMenu => selectMenu.id === menuId);
    }
    getSelectMenus() {
        return get(this.resp, 'selectMenus', []);
    }
    getMenuItemMap() {
        return get(this.resp, 'menuItemMap', {});
    }
    getItem(itemValueId) {
        return this.getMenuItemMap()[itemValueId];
    }
    getVariationsMap() {
        return get(this.resp, 'variationsMap', {});
    }
    getVariation(variationId) {
        return this.getVariationsMap()[variationId];
    }
    getMedia(imgIndex) {
        return this.getMediaList()[imgIndex];
    }
    getMediaList() {
        return get(this.resp, 'mediaList', []);
    }
    getImage(imageId) {
        const media = this.getMediaList().find(item => get(item, 'image.originalImg.imageId', '') === imageId);
        return get(media, 'image.originalImg', null);
    }
    getEEK() {
        return get(this.itemCard, '__search.eekIcon', null);
    }
    getEEKTire() {
        return get(this.itemCard, '__search.eekTire', null);
    }
    doesMenuHavePictures(menuId) {
        const menu = this.getMenu(menuId);
        return get(menu, 'hasPictures', false);
    }
    getMenuWithPictures() {
        return this.getSelectMenus().find(menu => menu.hasPictures);
    }
    getMenuErrorMsg(menuId) {
        const menu = this.getMenu(menuId);
        return get(menu, 'accessibilityText.text', '');
    }
    getMenuItemCombinations() {
        return get(this.resp, 'menuItemCombinations', {});
    }
    getMenuItemPictureIndexMap() {
        return get(this.resp, 'menuItemPictureIndexMap', {});
    }
    isValidForCombo(item, selectedIds) {
        if (!item) return {};

        if (!selectedIds || !selectedIds.length) {
            // No selectedValueIds means all menu items have been unselected.
            // (user has reset all menus to default '-Select-' non-item).
            return {
                isValidForCombo: true,
                isComboOutOfStock: false
            };
        }

        // Remove selectedIds that belong to the same menu as the item passed in,
        // because an items combinationsMap will not contain valueIds that belong to its menu.
        // For instance the combinationsMap for a "Size" item will not have a size valueId;
        // It will only have valueIds other than "Size".
        const sameMenu = this.getSelectMenus().find(menu => menu.menuItemValueIds.includes(item.valueId)) || [];
        const menuItemValueIds = sameMenu.menuItemValueIds || [];
        const otherSelectedIds = selectedIds.filter(id => !menuItemValueIds.includes(id));

        const comboList = this.getMenuItemCombinations()[item.valueId] || [];

        // When there's only one menu, the comboList should be empty.
        // If there's only one menu, there's no combo to validate.
        if (!comboList.length) {
            return {
                isValidForCombo: true,
                isComboOutOfStock: false
            };
        }

        let isValidForCombo = false;
        let isComboOutOfStock = false;
        for (let i = 0, n = comboList.length; i < n; i++) {
            const combo = comboList[i];

            // All the otherSelectedIds must be found in the combinationsMap to be validForCombo
            let foundCount = 0;
            const comboValueIds = Object.values(combo.combinationsMap);
            otherSelectedIds.forEach(id => {
                if (comboValueIds.includes(id)) {
                    foundCount++;
                }
            });
            if (foundCount === otherSelectedIds.length) { // All selectedIds found in combo
                isValidForCombo = true;
                // isOutOfStock property is not always present in model. Convert to boolean.
                isComboOutOfStock = !!combo.isOutOfStock;

                // SelectedIds could make a partial combo.
                // For instance selectedIds could be 1 and 2, while a full combo is 1, 2, and 3;
                // A partial combo is valid.
                // Try to find partial combo that is not out-of-stock.
                if (isValidForCombo && !isComboOutOfStock) {
                    break;
                }
            }
        }
        return {
            isValidForCombo,
            isComboOutOfStock
        };
    }
    getMenuItemValueIdByImage(imageId) {
        const getImageId = media => get(media, 'image.originalImg.imageId', null);
        // eslint-disable-next-line no-shadow
        const getImageIndex = imageId => this.getMediaList().findIndex(media => getImageId(media) === imageId);
        const imageIndex = getImageIndex(imageId);
        let foundMenuValueIdString;
        const itemPicMap = this.getMenuItemPictureIndexMap();
        // eslint-disable-next-line guard-for-in
        for (const menuItemValueId in itemPicMap) {
            const imageIndexes = itemPicMap[menuItemValueId];
            if (imageIndexes.includes(imageIndex)) {
                foundMenuValueIdString = menuItemValueId;
            }
        }
        const valueId = parseInt(foundMenuValueIdString);
        return isNaN(valueId) ? -1 : valueId;
    }
    getShippingReturns() {
        const shippingReturns = [];
        const shipping = this.getShipping();
        const freeReturns = this.getFreeReturns();
        if (shipping) {
            shippingReturns.push(shipping);
        }
        if (freeReturns) {
            shippingReturns.push(freeReturns);
        }
        return shippingReturns.join(' | ');
    }
    getTitleTag() {
        return get(this.itemCard, '__search.titleTags.0', []);
    }
    getTitle() {
        return {
            text: get(this.resp, 'title.textSpans.0.text', ''),
            url: get(this.itemCard, 'title.action.URL', ''),
            tracking: getTrackingString(this.resp, 'title.action.trackingList', [])
        };
    }
    getViewItemButton() {
        return {
            label: get(this.resp, 'viewAllDetailsButton.text', ''),
            url: get(this.itemCard, 'title.action.URL', ''),
            tracking: getTrackingString(this.resp, 'viewAllDetailsButton.action.trackingList', [])
        };
    }
    getFreeReturns() {
        return get(this.itemCard, '__search.freeReturnsNoFee.textSpans.0.text', '');
    }
    getShipping() {
        return get(this.itemCard, 'logisticsCost.textSpans.0.text', '');
    }
    getHeroImage() {
        const mediaList = this.getMediaList();
        return {
            url: get(mediaList, '0.image.originalImg.URL', ''),
            title: get(mediaList, '0.image.originalImg.title', '')
        };
    }
}
